import React, { useCallback, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import { Message } from 'semantic-ui-react';

import clsx from 'clsx';
import _isEmpty from 'lodash/isEmpty';
import SimpleMdeReact from 'react-simplemde-editor';

import onUpdate from 'hooks/useOnUpdate/use-on-update';

import { DEFAULT_OPTIONS } from './MarkdownField.constants';
import {
  generateMarkdownFieldImageToolbar,
  createToolbarPlugins,
  getPluginButtons,
  getPluginComponents,
  getMarkdownValidationMessage,
  createTextOnlyToolbarPlugins,
  customMarkdownPreviewRender,
} from './MarkdownField.utils';
import { MarkdownHighlightingKey } from './components';

import './MarkdownField.scss';

const propTypes = {
  id: PropTypes.string,
  value: PropTypes.string,
  originalValue: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.shape({}),
  toolbarPlugins: PropTypes.arrayOf(PropTypes.func),
  toolbarGenerator: PropTypes.func,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  onValidationError: PropTypes.func,
  highlightWords: PropTypes.arrayOf(PropTypes.string),
  disableMarkdownToolbar: PropTypes.bool,
};

const defaultProps = {
  id: undefined,
  value: '',
  originalValue: '',
  options: {},
  toolbarPlugins: [],
  toolbarGenerator: generateMarkdownFieldImageToolbar,
  disabled: false,
  loading: false,
  onValidationError: () => {},
  highlightWords: [],
  disableMarkdownToolbar: false,
};

export default function MarkdownField({
  id,
  value,
  originalValue,
  onChange: onChangeProp,
  options,
  toolbarPlugins,
  toolbarGenerator,
  disabled,
  loading,
  onValidationError,
  highlightWords,
  disableMarkdownToolbar,
}) {
  const prevValue = useRef(value);

  const [validationErrorMessage, setValidationErrorMessage] = useState('');

  const onChange = useCallback(
    async (newValue) => {
      if (prevValue.current !== newValue) {
        onChangeProp(newValue);
        prevValue.current = newValue;

        const message = await getMarkdownValidationMessage(newValue);
        setValidationErrorMessage(message);
      }
    },
    [onChangeProp],
  );

  onUpdate(() => {
    onValidationError({ message: validationErrorMessage });
  }, [validationErrorMessage]);

  function activateSideBySidePreview(instance) {
    const { autoDisplaySideBySidePreview } = { ...DEFAULT_OPTIONS, ...options };
    if (autoDisplaySideBySidePreview) {
      instance?.toggleSideBySide();
    }
  }

  const toolbarPluginsToRender = createToolbarPlugins(toolbarPlugins);
  const toolbarWithPlugins = disableMarkdownToolbar
    ? createTextOnlyToolbarPlugins()
    : toolbarGenerator(getPluginButtons(toolbarPluginsToRender));
  const pluginComponents = getPluginComponents(toolbarPluginsToRender);

  const finalClassName = clsx({
    'markdown-field': true,
    disabled,
    loading,
    error: !_isEmpty(validationErrorMessage),
  });

  const showHighlightingKey = !_isEmpty(highlightWords) || !_isEmpty(originalValue);

  return (
    <div className={finalClassName}>
      {showHighlightingKey && <MarkdownHighlightingKey />}
      <SimpleMdeReact
        id={id}
        value={value}
        onChange={onChange}
        getMdeInstance={activateSideBySidePreview}
        options={{
          ...DEFAULT_OPTIONS,
          ...options,
          toolbar: toolbarWithPlugins,
          previewRender: (plainText) =>
            customMarkdownPreviewRender(plainText, originalValue, highlightWords),
        }}
      />
      {pluginComponents}
      {!_isEmpty(validationErrorMessage) && (
        <Message negative show>
          {validationErrorMessage}
        </Message>
      )}
    </div>
  );
}

MarkdownField.propTypes = propTypes;
MarkdownField.defaultProps = defaultProps;
